// src/hooks/useAuth.js
import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { 
  loginAsync, 
  logoutAsync, 
  clearError, 
  clearAuth, 
  updateUserProfile, 
  validateToken 
} from '../store/authSlice';
import { 
  hasRole, 
  hasMinimumRole, 
  hasPermission, 
  hasAnyPermission, 
  hasAllPermissions, 
  canAccessRoute, 
  isAuthenticated as checkIsAuthenticated,
  getUserDisplayName,
  getUserInitials,
  getRoleDisplayName,
  isAdmin,
  isSuperAdmin,
  isUser,
  isDriver
} from '../utils/auth';

/**
 * Custom hook for authentication state and actions
 * Provides a comprehensive interface for authentication operations
 */
export const useAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  
  // Authentication actions
  const login = useCallback(async (credentials) => {
    try {
      const result = await dispatch(loginAsync(credentials)).unwrap();
      return { success: true, data: result };
    } catch (error) {
      return { success: false, error };
    }
  }, [dispatch]);
  
  const logout = useCallback(async () => {
    try {
      await dispatch(logoutAsync()).unwrap();
      return { success: true };
    } catch (error) {
      // Even if server logout fails, clear local state
      dispatch(clearAuth());
      return { success: true };
    }
  }, [dispatch]);
  
  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);
  
  const updateProfile = useCallback((profileData) => {
    dispatch(updateUserProfile(profileData));
  }, [dispatch]);
  
  const checkTokenValidity = useCallback(() => {
    dispatch(validateToken());
  }, [dispatch]);
  
  // Derived authentication state
  const isAuthenticated = checkIsAuthenticated(auth);
  const user = auth.user;
  const token = auth.token;
  const isLoading = auth.isLoading;
  const error = auth.error;
  
  // User information helpers
  const userDisplayName = getUserDisplayName(user);
  const userInitials = getUserInitials(user);
  const userRole = user?.role;
  const userRoleDisplayName = getRoleDisplayName(userRole);
  
  // Role checking functions
  const checkHasRole = useCallback((role) => hasRole(user, role), [user]);
  const checkHasMinimumRole = useCallback((minimumRole) => hasMinimumRole(user, minimumRole), [user]);
  const checkIsAdmin = useCallback(() => isAdmin(user), [user]);
  const checkIsSuperAdmin = useCallback(() => isSuperAdmin(user), [user]);
  const checkIsUser = useCallback(() => isUser(user), [user]);
  const checkIsDriver = useCallback(() => isDriver(user), [user]);
  
  // Permission checking functions
  const checkHasPermission = useCallback((permission) => hasPermission(user, permission), [user]);
  const checkHasAnyPermission = useCallback((permissions) => hasAnyPermission(user, permissions), [user]);
  const checkHasAllPermissions = useCallback((permissions) => hasAllPermissions(user, permissions), [user]);
  const checkCanAccessRoute = useCallback((route) => canAccessRoute(user, route), [user]);
  
  // Auto-validate token on mount and periodically
  useEffect(() => {
    if (token) {
      checkTokenValidity();
      
      // Set up periodic token validation (every 5 minutes)
      const interval = setInterval(() => {
        checkTokenValidity();
      }, 5 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, [token, checkTokenValidity]);
  
  return {
    // Authentication state
    isAuthenticated,
    isLoading,
    error,
    user,
    token,
    
    // User information
    userDisplayName,
    userInitials,
    userRole,
    userRoleDisplayName,
    
    // Authentication actions
    login,
    logout,
    clearError: clearAuthError,
    updateProfile,
    checkTokenValidity,
    
    // Role checking
    hasRole: checkHasRole,
    hasMinimumRole: checkHasMinimumRole,
    isAdmin: checkIsAdmin,
    isSuperAdmin: checkIsSuperAdmin,
    isUser: checkIsUser,
    isDriver: checkIsDriver,
    
    // Permission checking
    hasPermission: checkHasPermission,
    hasAnyPermission: checkHasAnyPermission,
    hasAllPermissions: checkHasAllPermissions,
    canAccessRoute: checkCanAccessRoute
  };
};

/**
 * Custom hook for permission-based component rendering
 * Returns boolean indicating if user has required permissions
 */
export const usePermissions = (requiredPermissions = []) => {
  const { user, hasAnyPermission } = useAuth();
  
  const hasRequiredPermissions = useCallback(() => {
    if (!requiredPermissions.length) return true;
    return hasAnyPermission(requiredPermissions);
  }, [requiredPermissions, hasAnyPermission]);
  
  return {
    hasPermissions: hasRequiredPermissions(),
    user,
    checkPermission: hasAnyPermission
  };
};

/**
 * Custom hook for role-based component rendering
 * Returns boolean indicating if user has required role level
 */
export const useRole = (requiredRole = null, exact = false) => {
  const { user, hasRole, hasMinimumRole } = useAuth();
  
  const hasRequiredRole = useCallback(() => {
    if (!requiredRole) return true;
    return exact ? hasRole(requiredRole) : hasMinimumRole(requiredRole);
  }, [requiredRole, exact, hasRole, hasMinimumRole]);
  
  return {
    hasRole: hasRequiredRole(),
    user,
    userRole: user?.role
  };
};

/**
 * Custom hook for route protection
 * Returns boolean indicating if user can access the route
 */
export const useRouteAccess = (route) => {
  const { canAccessRoute, isAuthenticated } = useAuth();
  
  const canAccess = useCallback(() => {
    if (!isAuthenticated) return false;
    return canAccessRoute(route);
  }, [route, canAccessRoute, isAuthenticated]);
  
  return {
    canAccess: canAccess(),
    isAuthenticated
  };
};

/**
 * Custom hook for authentication status monitoring
 * Provides detailed authentication status information
 */
export const useAuthStatus = () => {
  const auth = useSelector(state => state.auth);
  const { isAuthenticated, user } = useAuth();
  
  return {
    isAuthenticated,
    isLoading: auth.isLoading,
    hasError: Boolean(auth.error),
    error: auth.error,
    loginAttempts: auth.loginAttempts,
    lastLoginAttempt: auth.lastLoginAttempt,
    user,
    sessionActive: isAuthenticated && Boolean(auth.token)
  };
};

export default useAuth;
